#!/usr/bin/env node
// pzx.mjs — the spherical triangle, computed. ONE triangle, every celestial problem.
//
// FOUNDER, 2026-08-02: "celestial navigation and spherical trigonometry will be a 3D AI engine job like
// ship construction and need to be really taught on trigonometry engine depth."
//
// He is right, and the analogue is exact. A hull is ONE geometry with many views — profile, plan, body,
// section — so we compute the hull once and project it. The celestial sphere is the same shape of
// problem: PZX is ONE triangle, and intercept, azimuth, amplitude, meridian altitude and great-circle
// sailing are that triangle solved for a different unknown. Taught as five separate formulae they are
// five things to memorise; taught as one triangle they are one thing to understand.
//
//   P  the elevated pole          PZ = 90 - latitude        (co-latitude)
//   Z  the observer's zenith      PX = 90 - declination     (polar distance)
//   X  the body                   ZX = 90 - altitude        (zenith distance)
//
//   angle P = LHA          angle Z = azimuth          angle X = parallactic angle
//
// WHY THIS IS COMPUTED AND NOT WRITTEN. The three gaps measured on 2026-08-02 — Napier's rules, the
// cosine formula, the sine formula's ambiguous case — are not prose gaps. A candidate who reads that
// the sine formula is ambiguous has learned a sentence; a candidate who solves a triangle and is handed
// TWO valid answers has met the ambiguity. So the engine returns what the mathematics returns, including
// when that is two answers or none.
//
// HONESTY RULES, same as the hull and the marker:
//   · An ambiguous case returns BOTH solutions, labelled. It never picks one.
//   · A degenerate triangle (body on the meridian, observer at the pole) is NAMED as degenerate and
//     routed to the correct simple case, never forced through a formula that divides by zero.
//   · Every result carries the formula it came from, so a candidate can check the working.
//
//   node pzx.mjs verify        # round-trip and known-value checks
//   node pzx.mjs demo          # the named problems, solved from one triangle
const D = Math.PI / 180;
const sin = (d) => Math.sin(d * D), cos = (d) => Math.cos(d * D), tan = (d) => Math.tan(d * D);
const asin = (x) => Math.asin(Math.max(-1, Math.min(1, x))) / D;
const acos = (x) => Math.acos(Math.max(-1, Math.min(1, x))) / D;
const atan2d = (y, x) => Math.atan2(y, x) / D;
const norm360 = (a) => ((a % 360) + 360) % 360;
const r6 = (x) => (x === null || x === undefined || Number.isNaN(x) ? null : +x.toFixed(6));

/** A degenerate triangle has a vanishing side or a body on the meridian — say so, do not divide by it. */
function degeneracy({ lat, dec, lha }) {
  const notes = [];
  if (Math.abs(Math.abs(lat) - 90) < 1e-9) notes.push('observer at the pole — the zenith IS the pole, PZ = 0 and the azimuth is undefined');
  if (Math.abs(norm360(lha)) < 1e-9) notes.push('body on the observer\'s meridian, upper transit — the triangle collapses to an arc; this is the MERIDIAN ALTITUDE case');
  if (Math.abs(norm360(lha) - 180) < 1e-9) notes.push('body on the meridian, lower transit — the triangle collapses to an arc');
  if (Math.abs(Math.abs(dec) - 90) < 1e-9) notes.push('body at the celestial pole — PX = 0');
  return notes;
}

/**
 * THE COSINE FORMULA — two sides and the included angle give the third side.
 *   cos(ZX) = cos(PZ)·cos(PX) + sin(PZ)·sin(PX)·cos(P)
 * With PZ = co-lat, PX = polar distance and P = LHA this is the calculated altitude of the intercept
 * method, and it is the single most useful line in celestial navigation.
 */
export function calculatedAltitude({ lat, dec, lha }) {
  const PZ = 90 - lat, PX = 90 - dec, P = norm360(lha);
  const cosZX = cos(PZ) * cos(PX) + sin(PZ) * sin(PX) * cos(P);
  const ZX = acos(cosZX);                       // zenith distance
  return {
    kind: 'computed', formula: 'cos(ZX) = cos(PZ)·cos(PX) + sin(PZ)·sin(PX)·cos(P)',
    sides: { PZ: r6(PZ), PX: r6(PX), ZX: r6(ZX) },
    Hc: r6(90 - ZX), zenith_distance: r6(ZX),
    degenerate: degeneracy({ lat, dec, lha }),
  };
}

/**
 * AZIMUTH by the cosine formula transposed — solve for angle Z rather than side ZX.
 *   cos(PX) = cos(PZ)·cos(ZX) + sin(PZ)·sin(ZX)·cos(Z)
 * Preferred over the sine formula BECAUSE it is unambiguous: cos runs monotonically over 0-180, so one
 * arccos gives one answer. The sine formula for azimuth is the classic trap and is offered separately.
 */
export function azimuth({ lat, dec, lha }) {
  const alt = calculatedAltitude({ lat, dec, lha });
  const PZ = 90 - lat, PX = 90 - dec, ZX = alt.zenith_distance;
  if (Math.abs(sin(PZ)) < 1e-12 || Math.abs(sin(ZX)) < 1e-12) {
    return { kind: 'null', reason: 'azimuth undefined — the observer is at the pole, or the body is in the zenith', degenerate: alt.degenerate };
  }
  const cosZ = (cos(PX) - cos(PZ) * cos(ZX)) / (sin(PZ) * sin(ZX));
  const Zangle = acos(cosZ);                    // 0-180, measured from the elevated pole
  // NO hemisphere branch. PZ = 90 − lat always measures to the NORTH celestial pole — for a southern
  // observer that is simply an obtuse side, which arccos handles — so the angle Z is measured from north
  // in BOTH hemispheres and one rule covers them. The first version branched on hemisphere and put a
  // body of 15°N declination, seen from 20°S, in the SOUTH-east at 116° when it bears north-east at 64°.
  // Caught by cross-checking against the independently-derived 3D sphere; a hand-rolled quadrant rule is
  // exactly the kind of thing that looks right in one hemisphere and is silently wrong in the other.
  const westward = norm360(lha) < 180;          // body west of the meridian → azimuth is 360 − Z
  const Zn = westward ? 360 - Zangle : Zangle;
  return {
    kind: 'computed', formula: 'cos(PX) = cos(PZ)·cos(ZX) + sin(PZ)·sin(ZX)·cos(Z)  →  solve for Z',
    Z_angle: r6(Zangle), Zn: r6(norm360(Zn)),
    note: 'the COSINE form is used because it is unambiguous; see sineFormulaAzimuth for why the sine form is not',
    degenerate: alt.degenerate,
  };
}

/**
 * THE SINE FORMULA and its AMBIGUOUS CASE — the third gap, and the reason it is a gap.
 *   sin(P)/sin(ZX) = sin(Z)/sin(PX)
 * arcsin returns an angle in 0-90, but the true angle may be its supplement. Both satisfy the equation.
 * This function therefore returns BOTH and refuses to choose — which is the whole lesson.
 */
export function sineFormulaAzimuth({ lat, dec, lha }) {
  const alt = calculatedAltitude({ lat, dec, lha });
  const PX = 90 - dec, ZX = alt.zenith_distance, P = norm360(lha);
  if (Math.abs(sin(ZX)) < 1e-12) return { kind: 'null', reason: 'body in the zenith — sin(ZX) = 0, the sine formula divides by zero' };
  const sinZ = sin(PX) * sin(P) / sin(ZX);
  if (Math.abs(sinZ) > 1) return { kind: 'null', reason: 'no solution — |sin Z| > 1' };
  const Z1 = asin(sinZ), Z2 = 180 - Z1;
  const truth = azimuth({ lat, dec, lha });
  return {
    kind: 'ambiguous', formula: 'sin(Z) = sin(PX)·sin(P) / sin(ZX)',
    solutions: [r6(Z1), r6(Z2)],
    why: 'sin(θ) = sin(180 − θ). Both angles satisfy the equation and the sine formula alone CANNOT distinguish them.',
    resolved_by: 'the cosine formula, which is monotonic over 0-180 and therefore unambiguous',
    cosine_says: truth.kind === 'computed' ? r6(truth.Z_angle) : null,
    // deliberately NOT resolved here — a candidate must see two answers and learn what disambiguates them
  };
}

/**
 * NAPIER'S RULES — the right-angled spherical triangle. Not a separate subject: it is PZX at the moment
 * one part becomes 90°, which happens constantly in practice (a body on the prime vertical, a body on
 * the horizon at amplitude, a great circle crossing the equator).
 *
 * The five circular parts in order, with the right angle omitted and the two sides ADJACENT to it
 * replaced by their complements:   a, b, co-C, co-c, co-A   (right angle at B)
 * Rule 1: sin(middle) = product of TANGENTS of adjacent parts
 * Rule 2: sin(middle) = product of COSINES of opposite parts
 */
export function napier({ parts }) {
  // parts: { a, b, A, C, c } with the right angle at B — supply any two, get the rest
  const P = { ...parts };
  const known = Object.keys(P).filter((k) => typeof P[k] === 'number');
  if (known.length < 2) return { kind: 'null', reason: 'a right-angled spherical triangle needs any TWO parts besides the right angle' };
  const steps = [];
  const has = (k) => typeof P[k] === 'number';
  for (let pass = 0; pass < 4; pass++) {
    // c is the hypotenuse (opposite the right angle at B)
    if (!has('c') && has('a') && has('b')) { P.c = acos(cos(P.a) * cos(P.b)); steps.push('cos(c) = cos(a)·cos(b)  [Rule 2, middle = co-c, opposites a and b]'); }
    if (!has('A') && has('a') && has('c')) { P.A = asin(sin(P.a) / sin(P.c)); steps.push('sin(a) = sin(A)·sin(c)  [Rule 2]'); }
    if (!has('C') && has('b') && has('c')) { P.C = asin(sin(P.b) / sin(P.c)); steps.push('sin(b) = sin(C)·sin(c)  [Rule 2]'); }
    if (!has('a') && has('A') && has('c')) { P.a = asin(sin(P.A) * sin(P.c)); steps.push('sin(a) = sin(A)·sin(c)  [Rule 2]'); }
    if (!has('b') && has('C') && has('c')) { P.b = asin(sin(P.C) * sin(P.c)); steps.push('sin(b) = sin(C)·sin(c)  [Rule 2]'); }
    if (!has('a') && has('b') && has('A')) { P.a = atan2d(tan(P.b) * Math.tan(P.A * D), 1) === 0 ? 0 : Math.atan(Math.tan(P.b * D) * Math.tan(P.A * D)) / D; steps.push('tan(a) = tan(b)·tan(A)  [Rule 1]'); }
    if (!has('C') && has('A') && has('c')) { P.C = acos(cos(P.c) === 0 ? 0 : (Math.cos(P.A * D) / Math.sin(P.C ? P.C * D : 1)) ) || P.C; }
  }
  const out = {}; for (const k of ['a', 'b', 'c', 'A', 'C']) out[k] = has(k) ? r6(P[k]) : null;
  const missing = Object.entries(out).filter(([, v]) => v === null).map(([k]) => k);
  return {
    kind: missing.length ? 'partial' : 'computed',
    right_angle_at: 'B', parts: out, steps,
    circular_parts: 'a, b, co-C, co-c, co-A — right angle omitted, the two parts adjacent to it replaced by complements',
    rules: ['sin(middle) = ∏ tan(adjacent)', 'sin(middle) = ∏ cos(opposite)'],
    ...(missing.length ? { unsolved: missing, reason: 'not determined by the parts given — supply another' } : {}),
  };
}

/** AMPLITUDE — PZX with the body ON the horizon (ZX = 90°), so the triangle is right-angled at Z. */
export function amplitude({ lat, dec }) {
  if (Math.abs(cos(lat)) < 1e-12) return { kind: 'null', reason: 'observer at the pole — every direction is south (or north)' };
  const s = sin(dec) / cos(lat);
  if (Math.abs(s) > 1) return { kind: 'null', reason: 'the body does not rise or set at this latitude — it is circumpolar or never rises' };
  const amp = asin(s);
  return {
    kind: 'computed', formula: 'sin(amplitude) = sin(dec) / cos(lat)   [PZX with ZX = 90°, right-angled at Z]',
    amplitude: r6(Math.abs(amp)),
    named: `${dec >= 0 ? 'N' : 'S'} ${Math.abs(r6(amp))}° — measured from EAST at rising, from WEST at setting, toward the elevated pole of the body's declination`,
    true_bearing_rising: r6(norm360(90 - amp)), true_bearing_setting: r6(norm360(270 + amp)),
    is_napier_case: true,
  };
}

/** MERIDIAN ALTITUDE — the degenerate PZX: LHA = 0, the triangle collapses to an arc on the meridian. */
export function meridianAltitude({ lat, dec }) {
  const zd = Math.abs(lat - dec);
  return {
    kind: 'computed', formula: 'latitude = declination ± zenith distance   [PZX degenerate: LHA = 0, no triangle to solve]',
    altitude: r6(90 - zd), zenith_distance: r6(zd),
    note: 'this is why mer-alt is the easiest sight in the book: on the meridian there is no triangle, only arithmetic along it',
    bearing: dec > lat ? 'body bears NORTH of the observer' : (dec < lat ? 'body bears SOUTH of the observer' : 'body in the zenith'),
  };
}

/** GREAT-CIRCLE SAILING — the SAME triangle with X as the destination instead of a body. */
export function greatCircle({ lat1, lon1, lat2, lon2 }) {
  const PZ = 90 - lat1, PX = 90 - lat2;
  let dlon = norm360(lon2 - lon1); if (dlon > 180) dlon -= 360;
  const P = Math.abs(dlon);
  const cosD = cos(PZ) * cos(PX) + sin(PZ) * sin(PX) * cos(P);
  const D_deg = acos(cosD);
  const distance_nm = r6(D_deg * 60);
  let course = null;
  if (Math.abs(sin(PZ)) > 1e-12 && Math.abs(sin(D_deg)) > 1e-12) {
    const cosC = (cos(PX) - cos(PZ) * cos(D_deg)) / (sin(PZ) * sin(D_deg));
    const C = acos(cosC);
    course = r6(norm360(dlon >= 0 ? C : 360 - C));
  }
  // the VERTEX — the highest latitude reached, found by Napier on the right-angled triangle at the vertex
  let vertexLat = null;
  if (course !== null) {
    const v = acos(Math.abs(sin(course) * cos(lat1)));
    vertexLat = r6(lat1 >= 0 ? v : -v);
  }
  return {
    kind: 'computed', formula: 'cos(D) = cos(PZ)·cos(PX) + sin(PZ)·sin(PX)·cos(d.long)   [the SAME cosine formula]',
    distance_nm, distance_deg: r6(D_deg), initial_course: course, vertex_latitude: vertexLat,
    note: 'identical to the altitude calculation — only the meaning of the points changes. X is a destination rather than a body.',
  };
}

// ── verification ────────────────────────────────────────────────────────────────────────────────
function verify() {
  const t = []; const ok = (n, pass, d) => t.push({ n, pass, d });

  // 1. round trip: altitude → back out LHA through the same formula
  const a = calculatedAltitude({ lat: 35, dec: 12, lha: 40 });
  const PZ = 55, PX = 78, ZX = a.zenith_distance;
  const backLha = acos((cos(ZX) - cos(PZ) * cos(PX)) / (sin(PZ) * sin(PX)));
  ok('cosine formula round-trips LHA', Math.abs(backLha - 40) < 1e-6, `${backLha.toFixed(9)} vs 40`);

  // 2. amplitude agrees with the cosine formula at altitude 0 (body on the horizon)
  const amp = amplitude({ lat: 30, dec: 20 });
  const bearing = amp.true_bearing_rising;
  const az = azimuth({ lat: 30, dec: 20, lha: 360 - acos(-tan(30) * tan(20)) });  // LHA at rising
  ok('amplitude matches the azimuth at rising', Math.abs(norm360(bearing) - norm360(az.Zn)) < 0.01,
     `amplitude ${bearing}° vs cosine-azimuth ${az.Zn}°`);

  // 3. mer-alt is the degenerate case of the cosine formula at LHA = 0
  const m = meridianAltitude({ lat: 40, dec: 15 });
  const c0 = calculatedAltitude({ lat: 40, dec: 15, lha: 0 });
  ok('mer-alt equals the cosine formula at LHA=0', Math.abs(m.altitude - c0.Hc) < 1e-6, `${m.altitude} vs ${c0.Hc}`);
  ok('and LHA=0 is reported as degenerate', c0.degenerate.length > 0, c0.degenerate[0] || '');

  // 4. the sine formula returns TWO answers and does not pick
  const s = sineFormulaAzimuth({ lat: 35, dec: 12, lha: 40 });
  ok('sine formula returns both solutions', s.kind === 'ambiguous' && s.solutions.length === 2,
     `${s.solutions?.join(' and ')}`);
  ok('one of them matches the cosine answer', s.solutions?.some((x) => Math.abs(x - s.cosine_says) < 1e-6),
     `cosine says ${s.cosine_says}`);

  // 5. Napier: a right-angled triangle solved from two parts, checked against the cosine formula
  const n = napier({ parts: { a: 30, b: 40 } });
  ok('Napier gives the hypotenuse cos(c)=cos(a)cos(b)', Math.abs(n.parts.c - acos(cos(30) * cos(40))) < 1e-6, `c = ${n.parts.c}`);

  // 6. great circle: a known long leg, and the equator degenerating to a rhumb line
  const gc = greatCircle({ lat1: 0, lon1: 0, lat2: 0, lon2: 90 });
  ok('equator great circle = 5400 nm and course 090', Math.abs(gc.distance_nm - 5400) < 1e-3 && Math.abs(gc.initial_course - 90) < 1e-6,
     `${gc.distance_nm} nm, course ${gc.initial_course}`);
  const gc2 = greatCircle({ lat1: 50, lon1: -5, lat2: 40, lon2: -74 });   // Lizard → New York, ~2900 nm
  ok('Lizard→New York is a plausible 2800-3100 nm', gc2.distance_nm > 2800 && gc2.distance_nm < 3100, `${gc2.distance_nm} nm`);

  // 7. circumpolar body has no amplitude — the engine must refuse, not return a number
  const noamp = amplitude({ lat: 70, dec: 60 });
  ok('circumpolar body returns a typed null, not a number', noamp.kind === 'null', noamp.reason || '');

  for (const x of t) console.log(`  ${x.pass ? 'PASS' : 'FAIL'}  ${x.n}${x.d ? '  — ' + x.d : ''}`);
  const bad = t.filter((x) => !x.pass).length;
  console.log(`\n  ${t.length - bad}/${t.length} ${bad ? '· ' + bad + ' FAILED' : '· clean'}`);
  return bad;
}

/* GUARDED, because this module runs in a BROWSER now — the Academy has to work at sea, so the
   spherical triangle is solved on the candidate's phone. `process` is undefined there, and an
   unguarded reference here does not fail at build time: rollup bundles it happily and the page
   dies on load with "process is not defined". Which is exactly what happened, 2026-08-10 — a green
   build that could not boot. The CLI below is a node convenience; it must be inert everywhere else. */
if (typeof process !== 'undefined' && process.argv && import.meta.url === `file://${process.argv[1]}`) {
  const cmd = process.argv[2] || 'verify';
  if (cmd === 'demo') {
    const g = { lat: 35, dec: 12, lha: 40 };
    console.log('  ONE triangle, lat 35N dec 12N LHA 40:');
    console.log('   altitude  ', JSON.stringify(calculatedAltitude(g).Hc));
    console.log('   azimuth   ', JSON.stringify(azimuth(g).Zn));
    console.log('   sine form ', JSON.stringify(sineFormulaAzimuth(g).solutions), '← two answers, by design');
    console.log('   amplitude ', JSON.stringify(amplitude({ lat: 35, dec: 12 }).named));
    console.log('   mer-alt   ', JSON.stringify(meridianAltitude({ lat: 35, dec: 12 }).altitude));
    console.log('   great circ', JSON.stringify(greatCircle({ lat1: 50, lon1: -5, lat2: 40, lon2: -74 }).distance_nm), 'nm');
  } else {
    process.exit(verify() ? 1 : 0);
  }
}
